import { put, all, select, takeLatest, takeEvery } from "redux-saga/effects";

import { actions } from "./ducks";
import {
  requestMiddleware,
  sseMiddleware,
  uploadFileMiddleware
} from "helpers/api";
import { LocalStorageProvider } from "services/LocalStorageProvider";
import { filterByAlias } from "helpers/filterByAlias";
import {
  addLayerProvider,
  prepareProviderFiles,
  prepareProviderInference,
  removeLayerProvider,
  removeProviderInference,
  reorderLayerProvider,
  runProviderInference,
  stopProviderInference
} from "services/api/layer-providers";
import { runInference, stopInference } from "services/api/map";
import { LayerAlias, MULTISPECTRAL_FILTERS_OPTIONS, UnitTypes } from "constant";
import features from "features";
import {
  // streamProvidersCompletionStatus,
  streamProvidersInferenceStatus
} from "services/sse/layer-providers";
import { getProviderPhotos } from "services/api/units";

// function* watchProvidersCompletionStatus({ payload }) {
//   const request = streamProvidersCompletionStatus;

//   const {
//     setStreamCompletionDisconnectExecutor: getStreamDisconnectExecutor,
//     setCompletionEvent: onEvent
//   } = actions;

//   const { providersCompletionDisconnectExecutor } = yield select(
//     (state) => state.layersManager
//   );

//   //Disconnect if actually connected
//   if (providersCompletionDisconnectExecutor)
//     yield providersCompletionDisconnectExecutor();

//   yield sseMiddleware({
//     ...payload,
//     request,
//     onEvent,
//     getStreamDisconnectExecutor
//   });
// }

//Can be called multiple times for sync statuses immediately
function* watchProvidersInferenceStatus({ payload }) {
  const request = streamProvidersInferenceStatus;
  const {
    setProvidersStatuses: onEvent,
    setStreamStatusesDisconnectExecutor: getStreamDisconnectExecutor
  } = actions;

  const { providersStatusesDisconnectExecutor } = yield select(
    (state) => state.layersManager
  );

  //Disconnect if actually connected
  if (providersStatusesDisconnectExecutor)
    yield providersStatusesDisconnectExecutor();

  yield sseMiddleware({
    ...payload,
    request,
    onEvent,
    getStreamDisconnectExecutor
  });
}

function* createProvider({ payload }) {
  const request = addLayerProvider;
  const isFile = !!payload.fields.file;

  const { createProviderSuccess: setSuccess, createProviderError: setError } =
    actions;

  function* onSuccess(res) {
    const newRes = JSON.parse(JSON.stringify(res));
    const provider = newRes.providers.pop();

    const newFields = isFile
      ? {
          ...payload.fields
        }
      : {
          project_id: parseInt(provider.data.project),
          task_id: provider.data.task
        };

    const newPayload = {
      ...payload,
      params: { ...payload.params, providerId: provider.id },
      fields: newFields
    };

    if (payload.onSuccess) yield payload.onSuccess(res);

    //Preparing for RGB2 is called by user
    if (payload.params.alias !== LayerAlias.RGB2)
      yield setProviderInference({ payload: newPayload });
  }

  if (payload.fields.file) {
    const newPayload = {
      ...payload,
      fields: { file_name: payload.fields.file.name }
    };

    yield requestMiddleware({
      ...newPayload,
      request,
      setSuccess,
      setError,
      onSuccess
    });
  } else {
    yield requestMiddleware({
      ...payload,
      request,
      setSuccess,
      setError,
      onSuccess
    });
  }
}

function* deleteProviderInference({ payload }) {
  const request = removeProviderInference;

  const { deleteProviderError: setError } = actions;

  if (!payload.fields?.isOnlyGeocore) {
    yield requestMiddleware({
      ...payload,
      request,
      setError,
      isReturnRequestPayload: true
    });
  }

  yield deleteProvider({ payload });
}

function* reorderProvider({ payload }) {
  const request = reorderLayerProvider;

  const { reorderProviderSuccess: setSuccess, reorderProviderError: setError } =
    actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError
  });
}

function* deleteProvider({ payload }) {
  const request = removeLayerProvider;

  const { deleteProviderSuccess: setSuccess, setLayerIsActive } = actions;

  function* onSuccess() {
    const { alias } = payload.params;

    yield put(
      setLayerIsActive({ params: { alias }, fields: { isActive: true } })
    );
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    onSuccess
  });
}

function* setProviderInference({ payload }) {
  const request = prepareProviderInference;

  const {
    setUploadInferenceFileCancelEvent: getUploadCancelExecutor,
    setUploadInferenceFileProgress: setUploadProgress
  } = actions;

  function* onSuccess() {
    yield watchProvidersInferenceStatus({ payload });
  }

  if (payload.fields?.file) {
    yield uploadFileMiddleware({
      ...payload,
      request,
      onSuccess,
      getUploadCancelExecutor,
      setUploadProgress
    });
  } else {
    yield requestMiddleware({
      ...payload,
      request,
      onSuccess
    });
  }
}

function* startProviderInference({ payload }) {
  const request = runProviderInference;
  const {
    startProviderInferenceSuccess: setSuccess,
    startProviderInferenceError: setError
  } = actions;

  function* onSuccess() {
    yield watchProvidersInferenceStatus({ payload });
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}
function* cancelProviderInference({ payload }) {
  const request = stopProviderInference;
  const {
    cancelProviderInferenceSuccess: setSuccess,
    cancelProviderInferenceError: setError
  } = actions;

  function* onSuccess() {
    yield watchProvidersInferenceStatus({ payload });
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}

function* startSensorFusingInference({ payload }) {
  const request = runInference;
  const {
    startSensorFusingInferenceSuccess: setSuccess,
    startSensorFusingInferenceError: setError
  } = actions;

  function* onSuccess() {
    yield watchProvidersInferenceStatus({ payload });
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    isReturnRequestPayload: true,
    onSuccess
  });
}

function* cancelSensorFusingInference({ payload }) {
  const request = stopInference;
  const {
    cancelSensorFusingInferenceSuccess: setSuccess,
    cancelSensorFusingInferenceError: setError
  } = actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    isReturnRequestPayload: true
  });
}

function* setLayersDefault({ payload }) {
  const { setLayerIsActive } = actions;

  const layersSavedState = LocalStorageProvider.projects.getProject({
    projectId: payload.params.id
  });
  const availableLayers = filterByAlias(
    Object.entries(layersSavedState).map(([key, value]) => ({
      alias: key,
      isActive: value
    })),
    payload.layers
  );

  yield all(
    availableLayers.map(({ alias, isActive }) =>
      put(
        setLayerIsActive({
          params: { ...payload.params, alias },
          fields: { isActive }
        })
      )
    )
  );

  if (layersSavedState?.[LayerAlias.MANUAL]?.["hazard-zones"])
    yield all(
      Object.entries(layersSavedState[LayerAlias.MANUAL]["hazard-zones"]).map(
        ([alias, isActive]) =>
          put(
            features.map.actions.setAriaIsActive({
              projectId: payload.params.id,
              alias,
              isActive
            })
          )
      )
    );

  yield watchProvidersInferenceStatus({
    payload: {
      params: {
        id: payload.params.id,
        alias: LayerAlias.SENSOR_FUSION
      }
    }
  } as any);
}

function* handleSetLayerIsActive({ payload }) {
  const { layers, providers } = yield select((state) => state.layersManager);
  const { id } = yield select((state) => state.map);
  const { alias } = payload.params;
  const { isActive, isGeneralActive } = payload.fields;
  const { setProvidersTilesIsActive, setProvidersMarkersIsActive } = actions;

  LocalStorageProvider.projects.setSensorsLayersState({
    projectId: id,
    updatedState: {
      [alias]: payload.fields.isActive
    }
  });

  const layerData = layers.find((layer) => layer.alias === alias);

  if (alias === LayerAlias.MULTISPECTRAL) {
    const { setLayerFilter } = actions;
    const filter = MULTISPECTRAL_FILTERS_OPTIONS[0];
    yield put(yield setLayerFilter({ filter, alias }));
  }

  if (isActive) {
    if (alias === LayerAlias.SENSOR_FUSION) {
      yield put(
        yield features.unitsManager.actions.fetchUnitsRequest({
          params: {
            projectId: id,
            type: UnitTypes.POINT,
            alias
          }
        })
      );
    } else {
      if (providers.length === 0) {
        yield put(
          features.unitsManager.actions.fetchUnitsRequest({
            params: {
              projectId: id,
              type: UnitTypes.POINT,
              alias
            }
          })
        );
      } else {
        yield put(
          features.unitsManager.actions.clearUnits({
            params: {
              type: UnitTypes.POINT,
              alias
            }
          })
        );
        yield all(
          providers
            .filter((provider) => provider.isActiveMarkers ?? true)
            .map((provider) =>
              put(
                features.unitsManager.actions.fetchUnitsRequest({
                  params: {
                    projectId: id,
                    type: UnitTypes.POINT,
                    alias,
                    provider: provider.id
                  }
                })
              )
            )
        );
      }
    }
  }

  if (isGeneralActive && layerData?.providers?.length > 0) {
    if (alias !== LayerAlias.SENSOR_FUSION) {
      yield all(
        layerData.providers.map((provider) => {
          return put(
            setProvidersTilesIsActive({
              providerId: provider.id,
              isActiveTiles: isActive ?? true,
              alias
            })
          );
        })
      );
      yield all(
        layerData.providers.map((provider) => {
          return put(
            setProvidersMarkersIsActive({
              providerId: provider.id,
              isActiveMarkers: isActive ?? true,
              alias
            })
          );
        })
      );
    }
  }
}

function* watchProvidersMarkersIsActive({ payload }) {
  const { alias, isActiveMarkers } = payload;

  const { layers } = yield select((state) => state.layersManager);
  const { updateLayerProviders } = actions;

  const layerData = layers.find((layer) => layer.alias === alias);

  yield put(
    yield updateLayerProviders({
      providers: layerData.providers.map((item) => ({
        ...item,
        isActiveMarkers
      })),
      alias
    })
  );
}

function* watchProvidersTilesIsActive({ payload }) {
  const { alias, isActiveTiles } = payload;

  const { layers } = yield select((state) => state.layersManager);
  const { updateLayerProviders } = actions;

  const layerData = layers.find((layer) => layer.alias === alias);

  yield put(
    yield updateLayerProviders({
      providers: layerData.providers.map((item) => ({
        ...item,
        isActiveTiles
      })),
      alias
    })
  );
}

function* uploadProviderFiles({ payload }) {
  const request = prepareProviderFiles;

  //TODO temporary solution
  const {
    setUploadInferenceFileCancelEvent: getUploadCancelExecutor,
    setUploadInferenceFileProgress: setUploadProgress,
    uploadProviderFilesSuccess: setSuccess
  } = actions;
  // const {
  //   setUploadProviderFilesCancelEvent: getUploadCancelExecutor,
  //   setUploadProviderFilesProgress: setUploadProgress
  // } = actions;

  // function* onSuccess(res) {
  //   console.log(res);
  // }

  yield uploadFileMiddleware({
    ...payload,
    request,
    // onSuccess,
    getUploadCancelExecutor,
    setUploadProgress,
    setSuccess,
    isReturnRequestPayload: true
  });
}

function* fetchProvidersPhotos({ payload }) {
  const request = getProviderPhotos;
  const {
    fetchProvidersPhotosSuccess: setSuccess,
    fetchProvidersPhotosError: setError
  } = actions;
  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    isReturnRequestPayload: true
  });
}

function* handleClearProvidersState() {
  const { providersStatusesDisconnectExecutor } = yield select(
    (state) => state.layersManager
  );

  if (providersStatusesDisconnectExecutor)
    yield providersStatusesDisconnectExecutor();
}

function* handleClearLayersState() {
  const { providersCompletionDisconnectExecutor } = yield select(
    (state) => state.layersManager
  );

  if (providersCompletionDisconnectExecutor)
    yield providersCompletionDisconnectExecutor();
}

function* handleRestoreDefaultState() {
  const { clearState } = actions;
  const {
    providersStatusesDisconnectExecutor,
    providersCompletionDisconnectExecutor
  } = yield select((state) => state.layersManager);

  if (providersCompletionDisconnectExecutor)
    yield providersCompletionDisconnectExecutor();

  if (providersStatusesDisconnectExecutor)
    yield providersStatusesDisconnectExecutor();

  yield put(clearState());
}

export default function* watchSaga() {
  yield takeLatest(actions.setLayers, setLayersDefault);
  yield takeLatest(actions.setProviders, watchProvidersInferenceStatus);
  yield takeLatest(actions.createProviderRequest, createProvider);
  yield takeLatest(actions.deleteProviderRequest, deleteProviderInference);
  yield takeLatest(actions.reorderProviderRequest, reorderProvider);
  yield takeLatest(
    actions.startProviderInferenceRequest,
    startProviderInference
  );
  yield takeLatest(
    actions.cancelProviderInferenceRequest,
    cancelProviderInference
  );
  yield takeLatest(
    actions.startSensorFusingInferenceRequest,
    startSensorFusingInference
  );
  yield takeLatest(
    actions.cancelSensorFusingInferenceRequest,
    cancelSensorFusingInference
  );
  yield takeEvery(actions.setLayerIsActive, handleSetLayerIsActive);
  yield takeEvery(actions.fetchProvidersPhotosRequest, fetchProvidersPhotos);
  yield takeEvery(actions.uploadProviderFilesRequest, uploadProviderFiles);

  yield takeLatest(
    actions.prepareProviderInferenceRequest,
    setProviderInference
  );

  yield takeLatest(actions.clearProvidersState, handleClearProvidersState);
  yield takeLatest(actions.clearLayersState, handleClearLayersState);
  yield takeLatest(actions.restoreDefaultState, handleRestoreDefaultState);
  yield takeEvery(
    actions.setProvidersMarkersIsActive,
    watchProvidersMarkersIsActive
  );
  yield takeEvery(
    actions.setProvidersTilesIsActive,
    watchProvidersTilesIsActive
  );

  // yield takeLatest(actions.setLayerFilter, handleRestoreDefaultState);
}
